import React, { useState, useEffect } from 'react';
import { ThermometerSun, Sun, Moon, Wind, Droplets, Users, DollarSign, Zap, Save, PlayCircle, PauseCircle, RotateCcw, Plus } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Zone {
  id: string;
  name: string;
  currentTemp: number;
  targetTemp: number;
  occupancy: number;
  humidity: number;
  mode: 'heating' | 'cooling' | 'auto' | 'off';
  schedule: {
    start: string;
    end: string;
    targetTemp: number;
  }[];
}

interface AutomationRule {
  id: string;
  name: string;
  condition: {
    type: 'occupancy' | 'time' | 'temperature' | 'humidity';
    operator: '>' | '<' | '=';
    value: number;
  };
  action: {
    type: 'setTemp' | 'setMode' | 'adjustTemp';
    value: string | number;
  };
  active: boolean;
}

interface EnergyMetrics {
  currentUsage: number;
  projectedSavings: number;
  baseline: number;
  co2Reduction: number;
}

const EnergyEfficiencyController: React.FC = () => {
  // Zone Management
  const [zones, setZones] = useState<Zone[]>([
    {
      id: 'zone1',
      name: 'Open Office North',
      currentTemp: 72,
      targetTemp: 72,
      occupancy: 45,
      humidity: 45,
      mode: 'cooling',
      schedule: [
        { start: '09:00', end: '17:00', targetTemp: 72 }
      ]
    },
    {
      id: 'zone2',
      name: 'Conference Rooms',
      currentTemp: 73,
      targetTemp: 73,
      occupancy: 12,
      humidity: 48,
      mode: 'cooling',
      schedule: [
        { start: '08:00', end: '18:00', targetTemp: 73 }
      ]
    }
  ]);

  // Automation Rules
  const [rules, setRules] = useState<AutomationRule[]>([
    {
      id: 'rule1',
      name: 'Occupancy-based Temperature',
      condition: { type: 'occupancy', operator: '<', value: 5 },
      action: { type: 'adjustTemp', value: 2 },
      active: true
    },
    {
      id: 'rule2',
      name: 'After Hours Setback',
      condition: { type: 'time', operator: '>', value: 18 },
      action: { type: 'setMode', value: 'auto' },
      active: true
    }
  ]);

  // Energy Metrics
  const [metrics, setMetrics] = useState<EnergyMetrics>({
    currentUsage: 45.2,
    projectedSavings: 12.5,
    baseline: 58.7,
    co2Reduction: 127
  });

  // Simulation States
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1);
  const [energyHistory, setEnergyHistory] = useState<any[]>([]);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [showNewRule, setShowNewRule] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);

  // Generate simulated energy data
  useEffect(() => {
    if (isSimulating) {
      const interval = setInterval(() => {
        const now = new Date();
        const newDataPoint = {
          time: now.toLocaleTimeString(),
          usage: metrics.currentUsage + (Math.random() * 2 - 1),
          baseline: metrics.baseline
        };
        
        setEnergyHistory(prev => [...prev.slice(-20), newDataPoint]);
        updateMetrics();
      }, 1000 / simulationSpeed);

      return () => clearInterval(interval);
    }
  }, [isSimulating, simulationSpeed]);

  // Handlers for zone control
  const handleTempChange = (zoneId: string, temp: number) => {
    setZones(zones.map(zone =>
      zone.id === zoneId ? { ...zone, targetTemp: temp } : zone
    ));
    updateMetrics();
  };

  const handleModeChange = (zoneId: string, mode: Zone['mode']) => {
    setZones(zones.map(zone =>
      zone.id === zoneId ? { ...zone, mode } : zone
    ));
    updateMetrics();
  };

  const handleScheduleChange = (zoneId: string, scheduleIndex: number, updates: Partial<Zone['schedule'][0]>) => {
    setZones(zones.map(zone =>
      zone.id === zoneId ? {
        ...zone,
        schedule: zone.schedule.map((s, i) =>
          i === scheduleIndex ? { ...s, ...updates } : s
        )
      } : zone
    ));
    updateMetrics();
  };

  // Handlers for automation rules
  const handleToggleRule = (ruleId: string) => {
    setRules(rules.map(rule =>
      rule.id === ruleId ? { ...rule, active: !rule.active } : rule
    ));
    updateMetrics();
  };

  const handleAddRule = (rule: Omit<AutomationRule, 'id'>) => {
    const newRule = { ...rule, id: `rule${rules.length + 1}` };
    setRules([...rules, newRule]);
    setShowNewRule(false);
    updateMetrics();
  };

  const handleUpdateRule = (ruleId: string, updates: Partial<AutomationRule>) => {
    setRules(rules.map(rule =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
    setEditingRule(null);
    updateMetrics();
  };

  // Simulation controls
  const toggleSimulation = () => {
    setIsSimulating(!isSimulating);
    if (!isSimulating) {
      updateMetrics();
    }
  };

  const updateMetrics = () => {
    // Calculate impact of current settings
    const activeRules = rules.filter(r => r.active).length;
    const avgTempDiff = zones.reduce((sum, zone) => 
      sum + Math.abs(zone.currentTemp - zone.targetTemp), 0) / zones.length;
    
    const newUsage = metrics.baseline * (1 - (activeRules * 0.05) - (avgTempDiff * 0.02));
    const savings = metrics.baseline - newUsage;
    
    setMetrics({
      currentUsage: newUsage,
      projectedSavings: savings,
      baseline: metrics.baseline,
      co2Reduction: savings * 2.5 // Simplified conversion factor
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Controls */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-4">
        <h2 className="text-2xl font-bold">Energy Efficiency Lab</h2>
        <div className="flex items-center space-x-4">
          <select
            value={simulationSpeed}
            onChange={(e) => setSimulationSpeed(Number(e.target.value))}
            className="border rounded px-2 py-1"
          >
            <option value={1}>1x Speed</option>
            <option value={2}>2x Speed</option>
            <option value={5}>5x Speed</option>
          </select>
          <button
            onClick={toggleSimulation}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {isSimulating ? (
              <>
                <PauseCircle className="w-5 h-5" />
                <span>Pause Simulation</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-5 h-5" />
                <span>Start Simulation</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Real-time Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Current Usage</div>
            <Zap className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold">{metrics.currentUsage.toFixed(1)} kWh</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Projected Savings</div>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-2xl font-bold">{metrics.projectedSavings.toFixed(1)} kWh</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">CO2 Reduction</div>
            <Wind className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-2xl font-bold">{metrics.co2Reduction.toFixed(1)} kg</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Active Rules</div>
            <Save className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold">{rules.filter(r => r.active).length}</div>
        </div>
      </div>

      {/* Energy Usage Chart */}
      {isSimulating && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4">Real-time Energy Usage</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={energyHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="baseline" 
                  stroke="#94a3b8" 
                  fill="#94a3b8" 
                  fillOpacity={0.1} 
                  name="Baseline"
                />
                <Area 
                  type="monotone" 
                  dataKey="usage" 
                  stroke="#22c55e" 
                  fill="#22c55e" 
                  fillOpacity={0.3} 
                  name="Current Usage"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Zone Control */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Zone Control</h3>
          <div className="space-y-4">
            {zones.map(zone => (
              <div 
                key={zone.id}
                className={`border rounded-lg p-4 ${selectedZone === zone.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setSelectedZone(zone.id)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-medium">{zone.name}</h4>
                    <div className="text-sm text-gray-500">
                      <Users className="w-4 h-4 inline mr-1" />
                      {zone.occupancy} occupants
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold">{zone.currentTemp}°</span>
                    <div className="flex flex-col space-y-1">
                      <button 
                        onClick={() => handleTempChange(zone.id, zone.targetTemp + 1)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ▲
                      </button>
                      <button 
                        onClick={() => handleTempChange(zone.id, zone.targetTemp - 1)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between items-center mb-4">
                  <div className="flex space-x-2">
                    {(['heating', 'cooling', 'auto', 'off'] as Zone['mode'][]).map(mode => (
                      <button
                        key={mode}
                        onClick={() => handleModeChange(zone.id, mode)}
                        className={`px-3 py-1 rounded ${
                          zone.mode === mode 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  {zone.schedule.map((schedule, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={schedule.start}
                        onChange={(e) => handleScheduleChange(zone.id, index, { start: e.target.value })}
                        className="border rounded px-2 py-1"
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={schedule.end}
                        onChange={(e) => handleScheduleChange(zone.id, index, { end: e.target.value })}
                        className="border rounded px-2 py-1"
                      />
                      <input
                        type="number"
                        value={schedule.targetTemp}
                        onChange={(e) => handleScheduleChange(zone.id, index, { targetTemp: Number(e.target.value) })}
                        className="w-16 border rounded px-2 py-1"
                      />
                      <span>°F</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Automation Rules */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Automation Rules</h3>
            <button
              onClick={() => setShowNewRule(true)}
              className="flex items-center text-blue-500 hover:text-blue-600"
            >
              <Plus className="w-5 h-5 mr-1" />
              Add Rule
            </button>
          </div>

          {showNewRule && (
            <div className="mb-4 border rounded-lg p-4">
              <h4 className="font-medium mb-2">New Automation Rule</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Rule Name"
                  className="w-full px-2 py-1 border rounded"
                  onChange={(e) => setEditingRule(prev => prev ? { ...prev, name: e.target.value } : null)}
                />
                <div className="grid grid-cols-3 gap-2">
                  <select
                    className="px-2 py-1 border rounded"
                    onChange={(e) => setEditingRule(prev => prev ? {
                      ...prev,
                      condition: { ...prev.condition, type: e.target.value as any }
                    } : null)}
                  >
                    <option value="">Condition Type</option>
                    <option value="occupancy">Occupancy</option>
                    <option value="time">Time</option>
                    <option value="temperature">Temperature</option>
                    <option value="humidity">Humidity</option>
                  </select>
                  <select
                    className="px-2 py-1 border rounded"
                    onChange={(e) => setEditingRule(prev => prev ? {
                      ...prev,
                      condition: { ...prev.condition, operator: e.target.value as any }
                    } : null)}
                  >
                    <option value="">Operator</option>
                    <option value=">">Greater Than</option>
                    <option value="<">Less Than</option>
                    <option value="=">Equals</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Value"
                    className="px-2 py-1 border rounded"
                    onChange={(e) => setEditingRule(prev => prev ? {
                      ...prev,
                      condition: { ...prev.condition, value: Number(e.target.value) }
                    } : null)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="px-2 py-1 border rounded"
                    onChange={(e) => setEditingRule(prev => prev ? {
                      ...prev,
                      action: { ...prev.action, type: e.target.value as any }
                    } : null)}
                  >
                    <option value="">Action Type</option>
                    <option value="setTemp">Set Temperature</option>
                    <option value="setMode">Set Mode</option>
                    <option value="adjustTemp">Adjust Temperature</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Action Value"
                    className="px-2 py-1 border rounded"
                    onChange={(e) => setEditingRule(prev => prev ? {
                      ...prev,
                      action: { ...prev.action, value: e.target.value }
                    } : null)}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      if (editingRule) {
                        handleAddRule({ ...editingRule, active: true });
                        setShowNewRule(false);
                        setEditingRule(null);
                      }
                    }}
                    className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setShowNewRule(false);
                      setEditingRule(null);
                    }}
                    className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {rules.map(rule => (
              <div key={rule.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{rule.name}</h4>
                    <div className="text-sm text-gray-500">
                      When {rule.condition.type} {rule.condition.operator} {rule.condition.value}
                    </div>
                    <div className="text-sm text-gray-500">
                      Then {rule.action.type}: {rule.action.value}
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={rule.active}
                      onChange={() => handleToggleRule(rule.id)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnergyEfficiencyController; 